/**
 * PROP-E (WFS-FALLBACK-VAL-01) — Validación tipológica cruzada post-WFS fallback
 *
 * Comprueba si el uso de suelo Catastro (uso_cod DGC) del resultado de un
 * geocodificador de fallback es compatible con la tipología PTEL original.
 *
 * Si hay `usoCod` en metadata → validación real contra el adaptador `tipologiaACodigoCatastro`.
 * Si no hay `usoCod` (fallback WFS sin dato Catastro) → revisión recomendada por defecto.
 *
 * ⚠️ Esta función NO consulta Catastro en tiempo real; solo evalúa datos ya presentes
 * en `result.metadata`. Para consulta por punto/radio no existe API en CatastroGeocoderV2.
 *
 * @module services/geocoding/wfsFallbackValidator
 */
import { InfrastructureType } from '../../types/infrastructure';
import { TipologiaPTEL } from '../../types/tipologia';
import { tipologiaACodigoCatastro } from '../../types/catastroR2';
import type { GeocodingResult } from '../../types/infrastructure';

// Mapa InfrastructureType → TipologiaPTEL macro-19 (TUN3)
const INFRA_TYPE_TO_TIPOLOGIA: Partial<Record<InfrastructureType, TipologiaPTEL>> = {
  [InfrastructureType.HEALTH]:     TipologiaPTEL.SANITARIO,
  [InfrastructureType.EDUCATION]:  TipologiaPTEL.EDUCATIVO,
  [InfrastructureType.POLICE]:     TipologiaPTEL.POLICIAL,
  [InfrastructureType.FIRE]:       TipologiaPTEL.BOMBEROS,
  [InfrastructureType.EMERGENCY]:  TipologiaPTEL.EMERGENCIAS,
  [InfrastructureType.CULTURAL]:   TipologiaPTEL.CULTURAL,
  [InfrastructureType.SPORTS]:     TipologiaPTEL.DEPORTIVO,
  [InfrastructureType.MUNICIPAL]:  TipologiaPTEL.MUNICIPAL,
  [InfrastructureType.SOCIAL]:     TipologiaPTEL.SOCIAL,
  [InfrastructureType.FUEL]:       TipologiaPTEL.COMBUSTIBLE,
  [InfrastructureType.HYDRAULIC]:  TipologiaPTEL.HIDRAULICO,
  [InfrastructureType.ENERGY]:     TipologiaPTEL.ENERGIA,
  [InfrastructureType.INDUSTRIAL]: TipologiaPTEL.INDUSTRIAL,
};

/**
 * Determina si un código de uso DGC es compatible con una tipología PTEL.
 * Función pura, exportada para tests unitarios directos.
 *
 * @param usoCod  - Código de uso DGC (ej. 'Y', 'E', 'I')
 * @param infraType - Tipo de infraestructura PTEL
 * @returns true si es compatible o si la tipología no tiene restricción definida
 */
export function isUsoCompatibleConTipologia(
  usoCod: string,
  infraType: InfrastructureType,
): boolean {
  const tipologia = INFRA_TYPE_TO_TIPOLOGIA[infraType];
  if (!tipologia) return true; // tipología sin restricción → no incompatible
  const usosCompatibles = tipologiaACodigoCatastro(tipologia);
  return usosCompatibles.includes(usoCod);
}

/**
 * Aplica validación tipológica a un resultado de geocodificador de fallback.
 * Modifica `requiresManualReview` y `reviewReason` si detecta incompatibilidad.
 *
 * Reglas:
 * - Si `geocoderUsed` no empieza por 'fallback:' → devuelve resultado sin cambios.
 * - Si no hay `usoCod` en metadata (fallback WFS puro) → marca revisión recomendada.
 * - Si hay `usoCod` incompatible con la tipología → marca revisión con razón específica.
 * - Si `usoCod` es compatible → devuelve resultado sin cambios.
 *
 * No lanza excepciones. Devuelve siempre un objeto de resultado.
 *
 * @param result       - Resultado de geocodificación a evaluar
 * @param infraType    - Tipo de infraestructura PTEL original
 * @param geocoderUsed - Identificador del geocodificador usado (ej. 'fallback:wfs_health')
 */
export function applyWfsFallbackValidation(
  result: GeocodingResult,
  infraType: InfrastructureType,
  geocoderUsed: string,
): GeocodingResult {
  // Guard: solo actúa sobre resultados de fallback tipológico
  if (!geocoderUsed.startsWith('fallback:')) return result;

  const usoCod = (result.metadata?.usoCod as string | undefined) ?? '';

  // Sin usoCod (fallback WFS puro sin dato Catastro) → revisión recomendada
  if (!usoCod) {
    return {
      ...result,
      requiresManualReview: true,
      reviewReason:
        `Resultado de geocodificador alternativo (${geocoderUsed}) — ` +
        `verificar que las coordenadas corresponden a la infraestructura correcta`,
    };
  }

  // Con usoCod → validar compatibilidad real
  const compatible = isUsoCompatibleConTipologia(usoCod, infraType);
  if (!compatible) {
    return {
      ...result,
      requiresManualReview: true,
      reviewReason:
        `Uso Catastro '${usoCod}' posiblemente incompatible con tipología ` +
        `${infraType} (fallback: ${geocoderUsed})`,
    };
  }

  // Compatible → sin cambios
  return result;
}
